import React, { useState, useEffect } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import Analytics from 'utils/googleAnalytics'

import { useAppSelector } from 'redux/hooks'
import { store } from 'redux/store'
import { selectCardMap } from 'redux/deck/selectors'

import SaveService from 'services/save.service'
import CardService from 'services/card.service'
import ToastService from 'services/toast.service'

import { ColorAbr } from 'services/apiTypes/card.types'
import { CardType, FORMAT } from 'types/deck'

import BasicCard from 'components/card/BasicCard'
import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import QuantityInput from 'components/card/cardForm/quantityInput'
import Checkbox from 'components/formElements/Checkbox'
import PhatButton from 'components/formElements/PhatButton'
import LineLoader from 'components/elements/LineLoader'
import Icon from 'components/elements/Icon'

import { colorIdentity } from 'redux/deck/helpers/sorting'

import useGetManaBaseDeckData, {
  BASIC_TO_COLOR_MAP,
  COLOR_TO_BASIC_MAP,
  Options,
  DEFAULT_USER_OPTIONS,
} from 'components/deckPage/modals/autofillBasicLands/useGetBasicLandDeckData'

import styles from './autofillBasicLands.module.scss'

const LOCAL_STORAGE_KEY = 'autofillBasicLandsOptions'

type Props = {
  open: boolean
  onClose: () => void
}

const AutofillBasicLandsOverlay = ({ open, onClose }: Props) => {
  const [fetchingGenericBasics, setFetchingGenericBasics] = useState(true)
  const [updatingBasicsInDeck, setUpdatingBasicsInDeck] = useState(false)
  const [allBasicLands, setAllBasicLands] = useState(new Array<CardType>())

  const [options, setOptions] = useLocalStorage<Options>(LOCAL_STORAGE_KEY, DEFAULT_USER_OPTIONS)

  const cardMap = useAppSelector(selectCardMap)
  const basicLandsByName = Object.values(cardMap).reduce(
    (acc, card) => {
      if (!card.superTypes.includes('Basic')) return acc

      acc[card.name] = card

      return acc
    },
    {} as Record<string, CardType>,
  )

  useEffect(() => {
    if (!open) return

    setFetchingGenericBasics(true)

    // Populate all basic lands (including snow-covered & wastes). We may not use all of these, but we should have them in case we need them (prevents secondary fetch)
    // Limiting to format EDH because there are playtest cards with the basic supertype
    CardService.listCardsOnly({ superTypes: 'Basic', formatLegality: FORMAT.EDH })
      .then(setAllBasicLands)
      .catch(err => {
        console.error(err)

        ToastService.create('Unable to fetch initial basic lands', 'Basic land tool', 'error')
      })
      .finally(() => setFetchingGenericBasics(false))
  }, [open])

  const { castingCostColorData, nonBasicLandCount, basicLandCount, basicLandNamesInColorId, landRecsMap } =
    useGetManaBaseDeckData(options)

  const handleUpdateBasics = () => {
    setUpdatingBasicsInDeck(true)

    const { categories } = store.getState().deck

    const maindeckBasics = Object.values(cardMap).filter(
      card => card.superTypes.includes('Basic') && categories[card.categories[0]]?.includedInDeck,
    )
    const updatedCards = new Array<CardType>()

    for (const colorAbr of Object.keys(landRecsMap)) {
      const colorAbrTyped = colorAbr as ColorAbr // Stupid Object.keys not returning correct type
      const genericCardName = COLOR_TO_BASIC_MAP[colorAbrTyped]

      if (!genericCardName)
        return ToastService.create('Unable to match color abbr. with card name', 'Basic land tool', 'error')

      const cardName = options.preferSnow ? `Snow-Covered ${genericCardName}` : genericCardName

      // Getting the matching basic land if it's in the deck. Otherwise use the one we pulled down from search
      const basicsInTheDeckOfThisLandType = maindeckBasics.filter(card => card.name === cardName)
      const genericBasicOFThisLandType = allBasicLands.find(card => card.name === cardName)

      if (!genericBasicOFThisLandType)
        return ToastService.create('Unable to add unknown basic land card to deck', 'Basic land tool', 'error')

      // We don't want to try to reconcile this for users. Since we don't know which ones they'd prefer to have removed/ added more of, we'll let them see the data. But not update it on their behalf
      if (basicsInTheDeckOfThisLandType.length > 1)
        return ToastService.create(
          'Unable to auto update basic lands for decks with multiples of the same basic. (eg: You have 3 unique islands in your deck)',
          'Basic land tool',
          'error',
        )

      // No matching basics of this type in the deck. Push generic 'new' basic land
      if (basicsInTheDeckOfThisLandType.length === 0 && landRecsMap[colorAbrTyped].recommendedQty)
        updatedCards.push({ ...genericBasicOFThisLandType, qty: landRecsMap[colorAbrTyped].recommendedQty })

      // We only have 1 copy of the basic we need to edit, manually set the qty to the recommended qty
      if (basicsInTheDeckOfThisLandType.length === 1)
        updatedCards.push({ ...basicsInTheDeckOfThisLandType[0], qty: landRecsMap[colorAbrTyped].recommendedQty })
    }

    // If we're adding snow, removing all non-snow as visa versa
    const basicsThatMustBeRemoved = maindeckBasics.filter(card =>
      options.preferSnow ? !card.superTypes.includes('Snow') : card.superTypes.includes('Snow'),
    )
    for (const card of basicsThatMustBeRemoved) updatedCards.push({ ...card, qty: 0 })

    return SaveService.save(updatedCards, [], true)
      .then(() => {
        onClose()

        Analytics.signalEvent('Balanced basic lands')
        ToastService.create('Basic lands balanced', 'Deck service', 'success')
      })
      .catch(err => {
        console.error(err)

        ToastService.create('Unable to update deck', 'Basic land tool', 'error')
      })
      .finally(() => setUpdatingBasicsInDeck(false)) // Do something - probably a loader
  }

  // Generating lands that will be displayed
  // Since we pulled down any possible basic land, need to filter to only ones we care about
  const basicLands = allBasicLands
    .filter(card => {
      if (castingCostColorData.C < 1 && card.name.includes('Wastes')) return false
      if (options.preferSnow && card.superTypes.includes('Snow')) return true
      if (!options.preferSnow && !card.superTypes.includes('Snow')) return true

      return false
    })
    // Replace search cards with a card in the deck if it exists
    .map(card => basicLandsByName[card.name] || card)
    .sort(colorIdentity)

  const hasMoreThanOneCopyOfABasic = basicLands.reduce((acc, card) => {
    // If it was ever true, return true
    if (acc) return true

    // This card isn't in the deck yet. Because the card isn't in the deck, we know that we don't have ANY copies of this basic in the deck, so we don't need to worry about having multiples
    if (!card.deckRelationId) return false

    // If the total current quantity for any given basic land is larger than any one copy of a card in the deck, this proves that you have more than one unique copy of that basic in your deck
    return landRecsMap[BASIC_TO_COLOR_MAP[card.name] as ColorAbr].currentQty > card.qty
  }, false)

  return (
    <GlobalOverlayStack open={open} onClose={onClose} className={styles.container}>
      <div className={styles.container}>
        {fetchingGenericBasics && <LineLoader />}
        {!fetchingGenericBasics && (
          <>
            <div className={styles.options}>
              <h3>Basic land optimizer</h3>

              <QuantityInput
                id="land-selection-input"
                className={styles.quantityInput}
                quantity={options.targetLandCount}
                onQuantityUpdate={qty => setOptions({ ...options, targetLandCount: qty })}
                label="How many total lands would you like to play?"
              />

              <div className={styles.description}>
                <div>
                  <span>Current nonbasic land count:</span>
                  <span>{nonBasicLandCount}</span>
                </div>
                <div>
                  <span>Current basic land count:</span>
                  <span>{basicLandCount}</span>
                </div>
                <div>
                  <span>Total basic lands after operation:</span>
                  <span>{Math.abs(options.targetLandCount - nonBasicLandCount)}</span>
                </div>
              </div>

              <Checkbox
                checked={options.preferSnow}
                label="Use Snow-Covered basics"
                onChange={preferSnow => setOptions({ ...options, preferSnow })}
              />
              <Checkbox
                checked={options.requireAtLeastOneBasicPerColor}
                label="Require at least one of each basic land type in color identity"
                onChange={requireAtLeastOneBasicPerColor => setOptions({ ...options, requireAtLeastOneBasicPerColor })}
              />

              {hasMoreThanOneCopyOfABasic && (
                <div className={styles.warning}>
                  You have more than one unique copy of a basic in your deck. Since we won't know which printing to
                  edit, we'll leave that part to you. <br />
                  The above recommended numbers are correct, but you'll have to update the lands you want manually.
                </div>
              )}
            </div>

            <div className={styles.submit}>
              <PhatButton
                color="green"
                onClick={handleUpdateBasics}
                disabled={updatingBasicsInDeck || hasMoreThanOneCopyOfABasic}
                className={styles.saveButton}>
                Update basic lands
              </PhatButton>
            </div>

            <div className={styles.cardRow}>
              {basicLands.map(card => {
                const disabled =
                  !basicLandNamesInColorId[BASIC_TO_COLOR_MAP[card.name]] ||
                  (landRecsMap[BASIC_TO_COLOR_MAP[card.name] as ColorAbr].recommendedQty === 0 &&
                    landRecsMap[BASIC_TO_COLOR_MAP[card.name] as ColorAbr].currentQty === 0)

                return (
                  <div className={`${styles.card} ${disabled ? styles.faded : ''}`} key={card.name}>
                    <BasicCard card={card} />
                    <div className={styles.quantityBlock}>
                      <span>Quantity:</span>
                      <span>
                        {landRecsMap[BASIC_TO_COLOR_MAP[card.name] as ColorAbr].currentQty} <Icon name="arrow right" />
                        {landRecsMap[BASIC_TO_COLOR_MAP[card.name] as ColorAbr].recommendedQty}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </GlobalOverlayStack>
  )
}

export default AutofillBasicLandsOverlay
